// JobSummary.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Chip, Paper, Link, Typography, Stack, Box, Grid, MenuItem, Select } from '@mui/material';
import { CheckCircle, HourglassEmpty, Cancel, PlayCircleFilled } from "@mui/icons-material";
import { getbroker } from "../../../ApiSevices/UserMasterApis";

interface SubStatus {
  statusId: number;
  statusName: string;
  jobCount: number;
  Status_id: number
}

interface JobSummaryProps {
  summaryCountData: {
    totalCount?: number;
    jobTypeCounts?: Record<string, number>;
    jobStatusCounts?: Record<string, SubStatus[]>;
  } | null;
  onBrokerChange: (brokerId: number) => void;
}

const JobSummary: React.FC<JobSummaryProps> = ({ summaryCountData, onBrokerChange }) => {
  const navigate = useNavigate();
  const [selectedBrokerId, setSelectedBrokerId] = useState<number>(0);
  const [brokerList, setBrokerList] = useState<{ Broker_ID: number; vchBrokerName: string }[]>([]);


  const handleSubstatusClick = (statusId: number) => {
    const basePath = window.location.pathname.split('/')[1];
    navigate(`/${basePath}/jobs/joblisting?statusId=${statusId}`);
  };

  const fetchBrokers = async () => {
    try {
      const brokerData = await getbroker();
      setBrokerList(brokerData?.sort((a: any, b: any) => a?.vchBrokerName.localeCompare(b?.vchBrokerName)) || []);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  };

  useEffect(() => {
    fetchBrokers();
  }, []);

  if (!summaryCountData || !summaryCountData.jobTypeCounts || !summaryCountData.jobStatusCounts) {
    return <Box>
      <Typography
        variant="body2"
        sx={{ mb: 0.5, fontWeight: '500', }}
      >
        Select Broker
      </Typography>
                <Select
            value={selectedBrokerId}
            onChange={(e) => {
              const value = Number(e.target.value);
              setSelectedBrokerId(value);
              onBrokerChange(value);
            }}
            size="small"
            sx={{ minWidth: 160, fontSize: '12px' }}
            displayEmpty
            renderValue={(selected) =>
              Number(selected) === 0
                ? 'Select Broker'
                : brokerList?.find(
                  b => b.Broker_ID === Number(selected)
                )?.vchBrokerName || ''
            }
          >
            {brokerList?.map((broker) => (
              <MenuItem
                key={broker.Broker_ID}
                value={broker.Broker_ID}
                sx={{ fontSize: '12px' }}
              >
                {broker.vchBrokerName}
              </MenuItem>
            ))}
          </Select>
    </Box>;
  }


  const { totalCount, jobTypeCounts, jobStatusCounts } = summaryCountData;
  const { AR = 0, SSD = 0, MSD = 0 } = jobTypeCounts;

  // Status card data
  const statusCards = [
    {
      title: "InProgress",
      icon: <HourglassEmpty fontSize="large" />,
      color: "#DBEAFE",
      textColor: "#2563EB",
      count: jobStatusCounts?.InProgress?.reduce((sum, s) => sum + s.jobCount, 0) || 0,
      substatuses: jobStatusCounts?.InProgress || []
    },
    {
      title: "Success",
      icon: <PlayCircleFilled fontSize="large" />,
      color: "#E8F5E9",
      textColor: "#388E3C",
      count: jobStatusCounts?.Success?.reduce((sum, s) => sum + s.jobCount, 0) || 0,
      substatuses: jobStatusCounts?.Success || []
    },
    {
      title: "Dispatch",
      icon: <Cancel fontSize="large" />,
      color: "#E5E7EB",
      textColor: "#111827",
      count: jobStatusCounts?.Dispatch?.reduce((sum, s) => sum + s.jobCount, 0) || 0,
      substatuses: jobStatusCounts?.Dispatch || []
    },
    {
      title: "Dispatch Failed",
      icon: <CheckCircle fontSize="large" />,
      color: "#FDE8E8",
      textColor: "#D32F2F",
      count: jobStatusCounts?.Dispatch_Failed?.reduce((sum, s) => sum + s.jobCount, 0) || 0,
      substatuses: jobStatusCounts?.Dispatch_Failed || []
    }
  ];

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        {/* Select Broker */}
        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.5, fontWeight: 500 }}
          >
            Select Broker
          </Typography>

          <Select
            value={selectedBrokerId}
            onChange={(e) => {
              const value = Number(e.target.value);
              setSelectedBrokerId(value);
              onBrokerChange(value);
            }}
            size="small"
            sx={{ minWidth: 160, fontSize: '12px' }}
            displayEmpty
            renderValue={(selected) =>
              Number(selected) === 0
                ? 'Select Broker'
                : brokerList?.find(
                  b => b.Broker_ID === Number(selected)
                )?.vchBrokerName || ''
            }
          >
            {brokerList?.map((broker) => (
              <MenuItem
                key={broker.Broker_ID}
                value={broker.Broker_ID}
                sx={{ fontSize: '12px' }}
              >
                {broker.vchBrokerName}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Jobs Summary */}
        <Paper
          sx={{
            flex: 1,
            px: 3,
            py: 2,
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Typography fontWeight="bold" fontSize="15px">
              Jobs Summary
            </Typography>

            <Box textAlign="center">
              <Typography fontSize="11px" color="#555">
                No of Jobs
              </Typography>
              <Typography fontSize="11px" fontWeight="bold">
                {totalCount ?? 0}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Status Cards - Responsive Grid */}
      <Grid container spacing={1}>
        {statusCards.map((card, index) => (
          <Grid item xs={16} sm={10} md={2.95} key={index}>
            <Paper
              sx={{
                padding: '16px',
                borderRadius: "12px",
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: "#ffffff",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                border: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {/* <Box sx={{ bgcolor: "#dfd9d98c", padding: 1, borderRadius: 2, display: "inline-block" }}>
                  {card.icon}
                </Box> */}
                <Badge
                  sx={{
                    backgroundColor: card.color,
                    color: card.textColor,
                    padding: "6px 20px",
                    borderRadius: "25px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    height: '32px'
                  }}
                >
                  {card.title + ":"}&nbsp;&nbsp;{card.count}
                </Badge>
              </Box>



              {/* Substatuses list */}
              <Box sx={{ mt: 2, maxHeight: '120px', overflowY: 'auto' }}>
                {card.substatuses
                  .filter(substatus => substatus.jobCount > 0)
                  .map((substatus, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}
                    >
                      <Link
                        onClick={() => handleSubstatusClick(substatus?.statusId)}
                        sx={{
                          cursor: 'pointer',
                          fontSize: '11px',
                          color: card.textColor,
                          textDecorationColor: card.textColor,
                          '&:hover': {
                            textDecoration: 'underline',
                            textDecorationColor: card.textColor
                          }
                        }}
                      >
                        {substatus.statusName}
                      </Link>
                      <Chip
                        label={substatus.jobCount}
                        size="small"
                        sx={{
                          backgroundColor: card.color,
                          color: card.textColor,
                          minWidth: "30px",
                          fontSize: '11px',
                          height: '22px'
                        }}
                      />
                    </Box>
                  ))
                }
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

export default JobSummary;